import { DEFAULT_LOCALE, canonicalizeLocale } from '../locales/index.js';

/**
 * Shared `Intl` instance cache (ENG-378), behind `formatters.js` and
 * `currency.js`. `Intl` constructors are the expensive part of formatting
 * (locale data lookup, pattern compilation), so instances are kept per
 * constructor, locale and options and reused — a list of a hundred rows
 * builds one `DateTimeFormat`, not a hundred.
 */

const MAX_CACHED_FORMATTERS = 500;
const cache = new Map();

/** A formatter locale is always a canonical tag; anything else — including "use the browser's" — is the app default. */
export const formatterLocale = (locale) => canonicalizeLocale(locale) ?? DEFAULT_LOCALE;

/** Options serialised with their keys sorted, so `{ a, b }` and `{ b, a }` share one instance. */
const optionsKey = (options) => (options ? JSON.stringify(options, Object.keys(options).sort()) : '{}');

/**
 * A cached `new Ctor(locale, options)`. A `timeZone` the runtime does not
 * know (a show whose zone the backend misspelt) is the one option a caller
 * cannot vet in advance, so when the constructor rejects options that carry
 * one, the call is retried without it and the viewer's zone applies. Any
 * other invalid option is a programming error: it fails that retry too (or
 * the first attempt, when no `timeZone` is present) and throws. The
 * fallback instance is also cached under the rejected options, so a bad
 * zone costs one failed construction per page, not one per row.
 */
export const intlFormatter = (Ctor, locale, options) => {
  const tag = formatterLocale(locale);
  const key = `${Ctor.name} ${tag} ${optionsKey(options)}`;
  const cached = cache.get(key);
  if (cached) return cached;
  let instance;
  try {
    instance = new Ctor(tag, options);
  } catch (error) {
    if (!options?.timeZone) throw error;
    const { timeZone, ...rest } = options; // eslint-disable-line no-unused-vars
    instance = intlFormatter(Ctor, tag, rest);
  }
  if (cache.size >= MAX_CACHED_FORMATTERS) cache.clear();
  cache.set(key, instance);
  return instance;
};

/** Drop every cached instance (tests). */
export const resetFormatterCache = () => {
  cache.clear();
};
